import { useState, useEffect } from "react";
import { supabase } from "../ReusableComponents/supabaseClient";
import { toast } from "react-toastify";
import { logError } from '../Utilities/errorLogger';

export const selectedPoolDataStructure = {
  id: null,
  tournamentStatus: "",
  cutValue: "",
  currentRound: "",
};

export const fetchPoolDetailsById = async (poolId) => {
  try {
    const { data, error } = await supabase
      .from("pool")
      .select(`
        id,
        name,
        leaderboard_token,
        tournament_status,
        round_status,
        cut_value,
        time_zone,
        first_tee_time,
        current_round,
        max_players,
        num_player_used_for_scoring,
        all_players_must_make_cut,
        tie_breaker,
        tournament_fixture (
          id,
          name,
          start_date
        )
      `)
      .eq("id", poolId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    await logError({
      filePath: 'Leaderboard/LeaderboardHelper.js',
      functionName: 'fetchPoolDetailsById',
      errorType: 'Fetch Pool Details Error',
      errorMessage: error.message,
      stackTrace: error.stack
    });
    toast.error(`Error fetching pool details: ${error.message}`, { theme: "dark" });
    throw error;
  }
};

export const useFetchPoolLeaderboardOptions = (selectedPoolId) => {
  const [poolOptions, setPoolOptions] = useState([]);
  const [poolLoading, setLoading] = useState(true);

  useEffect(() => {
    if (!Number(localStorage.getItem("userId"))) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      const { data, error } = await supabase
        .from("team")
        .select("pool(id, name, current_round, cut_value, tournament_status, round_status, tournament_fixture(id, start_date)), owner(id)")
        .eq("owner", Number(localStorage.getItem("userId")));

      if (error) {
        toast.error(`Error: ${error}`, { theme: "dark" });
        setLoading(false);
        return;
      }

      if (data) {
        data.sort((a, b) => {
          const dateA = new Date(a.pool.tournament_fixture.start_date),
            dateB = new Date(b.pool.tournament_fixture.start_date);
          if (dateA - dateB === 0) {
            return a.pool.id - b.pool.id;
          }
          return dateA - dateB;
        });
        setPoolOptions(data);
      }
      setLoading(false);
    };

    fetchData();

    const poolTableSubscription = supabase
      .channel(`pool-changes-${selectedPoolId}`)
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "pool", filter: `id=eq.${selectedPoolId}` }, () => {
        fetchData();
      })
      .subscribe();

    return () => supabase.removeChannel(poolTableSubscription);
  }, [selectedPoolId]);

  return { poolOptions, poolLoading };
};

export const useLeaderboardData = (poolId) => {
  const [teams, setTeams] = useState([]);
  const [teamLineItems, setTeamLineItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!poolId) {
      setTeams([]);
      setTeamLineItems([]);
      return;
    }

    setLoading(true);

    const fetchData = async () => {
      try {
        // First fetch pool data to get max_players and tie-breaker config
        const { data: poolData, error: poolError } = await supabase.from("pool").select("max_players, tie_breaker").eq("id", poolId).single();

        if (poolError) {
          toast.error(`Error fetching pool data: ${poolError.message}`, { theme: "dark" });
          throw poolError;
        }

        // Fetch all teams for this pool
        const { data: teamsData, error: teamsError } = await supabase
          .from("team")
          .select("id, paid, missed_cut, rank, team_world_ranking_average, team_score_to_par, team_score_total, tie_breaker_difference, player_name, owner, line_items:teamLineItem(count), pool(id,tournament_status,current_round,max_players, tournament_fixture(start_date))")
          .eq("pool", poolId)
          .order("rank", { ascending: true, nullsLast: true });

        // Fetch owner names separately to avoid FK join issues with null owners
        if (teamsData) {
          const ownerIds = teamsData.map(t => t.owner).filter(Boolean);
          if (ownerIds.length > 0) {
            const { data: owners } = await supabase
              .from("user")
              .select("id, full_name")
              .in("id", ownerIds);
            const ownerMap = {};
            (owners || []).forEach(o => { ownerMap[o.id] = o; });
            teamsData.forEach(t => { t.ownerDetails = t.owner ? ownerMap[t.owner] : null; });
          }
        }


        if (teamsError) {
          toast.error(`Error fetching teams: ${teamsError.message}`, { theme: "dark" });
          throw teamsError;
        }

        if (teamsData) {
          teamsData.sort(createSortTeams(poolData.tie_breaker));
          console.log("teamsData", teamsData);
          setTeams(teamsData);

          // Only fetch line items if we have teams
          if (teamsData.length > 0) {
            const teamIds = teamsData.map((team) => team.id);
            const { data: lineItemsData, error: lineItemsError } = await supabase
              .from("teamLineItem")
              .select(
                "id, team, display_order, tournament_position, player_world_ranking, tee_time, holes_played, players_tournament_status, total_score, total_score_to_par, round_1_to_par, round_2_to_par, round_3_to_par, round_4_to_par, player:playersFixture!teamLineItem_player_fkey(id, player_name, world_ranking_position), pool(num_player_used_for_scoring, tournament_status, current_round)"
              )
              .in("team", teamIds);

            if (lineItemsError) {
              toast.error(`Error fetching line items: ${lineItemsError.message}`, { theme: "dark" });
              throw lineItemsError;
            }

            if (lineItemsData) {
              lineItemsData.sort(sortTeamLineItems);
              setTeamLineItems(lineItemsData);
            }
          }
        }
      } catch (error) {
        await logError({
          filePath: 'Leaderboard/LeaderboardHelper.js',
          functionName: 'useLeaderboardData',
          errorType: 'Leaderboard Data Fetch Error',
          errorMessage: error.message,
          stackTrace: error.stack
        });
        toast.error(`Error: ${error.message}`, { theme: "dark" });
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Set up subscriptions for both tables (unique channel names per pool)
    const teamTableSubscription = supabase
      .channel(`team-changes-${poolId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "team", filter: `pool=eq.${poolId}` }, () => {
        fetchData();
      })
      .subscribe();

    const teamLineItemTableSubscription = supabase
      .channel(`teamLineItem-changes-${poolId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "teamLineItem", filter: `pool=eq.${poolId}` }, () => {
        fetchData();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(teamTableSubscription);
      supabase.removeChannel(teamLineItemTableSubscription);
    };
  }, [poolId]);

  return { teams, teamLineItems, loading };
};

// Factory: returns a comparator that applies the pool's configured tie-breaker
// when teams share the same primary score (e.g., among MC teams).
const createSortTeams = (tieBreaker) => (a, b) => {
  // Handle null/undefined ranks
  if (!a.rank && !b.rank) return 0;
  if (!a.rank) return 1;
  if (!b.rank) return -1;

  const isMCA = String(a.rank).toUpperCase() === "MC";
  const isMCB = String(b.rank).toUpperCase() === "MC";

  const parseScore = (s) => {
    if (!s || s === "--") return Infinity;
    if (String(s).toUpperCase() === "E") return 0;
    return parseInt(s, 10) || Infinity;
  };

  const scoreA = parseScore(a.team_score_to_par);
  const scoreB = parseScore(b.team_score_to_par);

  // Sort teams with scoreTotal of "--" to the bottom
  if (scoreA === Infinity && scoreB !== Infinity) return 1;
  if (scoreA !== Infinity && scoreB === Infinity) return -1;

  // MC teams sit below all eligible teams
  if (isMCA && !isMCB) return 1;
  if (!isMCA && isMCB) return -1;

  // Apply pool's configured tie-breaker. Lower world ranking avg = better
  // (closer to #1); smaller tie_breaker_difference = closer guess.
  const tieBreakerCompare = () => {
    if (tieBreaker === "Average World Ranking") {
      const aWR = a.team_world_ranking_average ?? 999999;
      const bWR = b.team_world_ranking_average ?? 999999;
      if (aWR !== bWR) return aWR - bWR;
    } else if (tieBreaker === "Winning Score") {
      const aDiffRaw = parseFloat(a.tie_breaker_difference);
      const bDiffRaw = parseFloat(b.tie_breaker_difference);
      const aDiff = Number.isNaN(aDiffRaw) ? 999999 : aDiffRaw;
      const bDiff = Number.isNaN(bDiffRaw) ? 999999 : bDiffRaw;
      if (aDiff !== bDiff) return aDiff - bDiff;
    }
    return 0;
  };

  // Both MC: score-to-par first, then pool's tie-breaker, then total strokes
  if (isMCA && isMCB) {
    if (scoreA !== scoreB) return scoreA - scoreB;
    const tb = tieBreakerCompare();
    if (tb !== 0) return tb;
    const totalA = parseScore(a.team_score_total);
    const totalB = parseScore(b.team_score_total);
    return totalA - totalB;
  }

  // Eligible teams: trust backend-assigned rank (already tie-broken), with
  // score and pool's tie-breaker as fallback safety nets.
  const getNumericRank = (rank) => parseInt(rank.replace("T", ""), 10);
  const rankDifference = getNumericRank(a.rank) - getNumericRank(b.rank);
  if (rankDifference !== 0) return rankDifference;

  const scoreDifference = scoreA - scoreB;
  if (scoreDifference !== 0) return scoreDifference;

  const tb = tieBreakerCompare();
  if (tb !== 0) return tb;

  // Prioritize current user's team when all scores are 0 (pre-tournament view)
  if (a.team_score_total === "0" && b.team_score_total === "0") {
    const currentUserId = Number(localStorage.getItem("userId"));
    const isUserTeamA = (a.ownerDetails?.id || a.owner) === currentUserId;
    const isUserTeamB = (b.ownerDetails?.id || b.owner) === currentUserId;

    if (isUserTeamA) return -1;
    if (isUserTeamB) return 1;
  }

  return 0;
};

const sortTeamLineItems = (a, b) => {
  const scoreToNumeric = (score) => {
    if (score === "E" || score === "-" || score === "--") return 0;
    return parseInt(score, 10);
  };

  const totalScoreToNumeric = (score) => {
    if (score === "0" || score === "-" || score === "--") return Number.MAX_SAFE_INTEGER; // Push "0", "-", "--" to the bottom
    return parseInt(score, 10);
  };

  const isInactiveStatus = (status) => {
    return ["cut", "wd", "dq"].includes(status.toLowerCase());
  };

  const aScore = scoreToNumeric(a.total_score_to_par);
  const bScore = scoreToNumeric(b.total_score_to_par);

  const aTotalScore = totalScoreToNumeric(a.total_score);
  const bTotalScore = totalScoreToNumeric(b.total_score);

  const aInactive = isInactiveStatus(a.players_tournament_status);
  const bInactive = isInactiveStatus(b.players_tournament_status);

  // Check if both players are inactive or active, then sort based on total score to par
  if (aInactive === bInactive) {
    if (aScore !== bScore) {
      return aScore - bScore; // Sort by score to par value
    }

    // If score to par values are equal, sort by total score
    return aTotalScore - bTotalScore;
  }

  // If only one player is inactive, sort to put inactive players lower
  return aInactive ? 1 : -1;
};
